import { useMemo, useState } from 'react'
import { LinkContainer } from 'react-router-bootstrap'
import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import AuthService from '../../utilities/services/auth.service'
import {
  canViewAccountingReport,
  isHensonFundReportEnabled,
} from '@blocktickets/shared-utils'

import './reportingWrapper.scss'

const REPORTING_SECTIONS = [
  {
    id: 'attendance-access',
    title: 'Attendance & Access',
    reports: [
      {
        id: 'access-control-summary',
        title: 'Access control summary',
        description: 'Scanned vs. unscanned tickets, entry by gate and time.',
        path: '/reporting/access-control-summary-v2',
        icon: 'receipt',
        accent: 'accent-cyan',
      },
      {
        id: 'access-pass-holders',
        title: 'Access pass holders',
        description:
          'Printable roster of access pass holders with name, email, phone, and address.',
        path: '/access-pass-holders',
        icon: 'ticket',
        accent: 'accent-amber',
        accessPassOnly: true,
      },
      {
        id: 'time-stamp-report',
        title: 'Time stamp report',
        description: 'Entry and exit timestamps per ticket holder.',
        path: '/time-stamp-report',
        icon: 'clock',
        accent: 'accent-green',
      },
      {
        id: 'daily-ticket-counts',
        title: 'Daily ticket counts',
        description: 'Tickets issued, scanned, and voided per day.',
        path: '/daily-tickets-count',
        icon: 'ticket',
        accent: 'accent-amber',
      },
      {
        id: 'attendance-intelligence',
        title: 'Attendance intelligence',
        description:
          'Scan timing, section turnout, buyer segments, and no-show impact for a selected event.',
        path: '/attendance-intelligence',
        icon: 'chart',
        accent: 'accent-pink',
      },
      {
        id: 'comp-intelligence',
        title: 'Complimentary intelligence',
        description:
          'Comp issuance, scans, and no-shows by buyer type, event, and source.',
        path: '/comp-intelligence',
        icon: 'chart',
        accent: 'accent-cyan',
      },
      {
        id: 'weather-event-analytics',
        title: 'Weather event analytics',
        description:
          'Weather severity, attendance impact, walk-up sales, scan rate, and staffing guidance.',
        path: '/weather-event-analytics',
        icon: 'chart',
        accent: 'accent-cyan',
      },
    ],
  },
  {
    id: 'sales-revenue',
    title: 'Sales & Revenue',
    reports: [
      {
        id: 'sales-attendance',
        title: 'Event sales & attendance',
        description: 'Revenue and headcount across events and ticket types.',
        path: '/sales-attendance',
        icon: 'chart',
        accent: 'accent-cyan',
      },
      {
        id: 'sales-velocity',
        title: 'Sales velocity',
        description:
          'Venue seatmap heatmap of sell-through, demand, and pricing by section.',
        path: '/sales-velocity',
        icon: 'chart',
        accent: 'accent-amber',
      },
      {
        id: 'geographic-sales-intelligence',
        title: 'Geographic sales intelligence',
        description:
          'Buyer heatmap, distance from venue, local vs. out-of-market, and U.S. state sales.',
        path: '/geographic-sales-intelligence',
        icon: 'chart',
        accent: 'accent-cyan',
      },
      {
        id: 'pricing-level-performance',
        title: 'Pricing level performance',
        description:
          'Matrix of sell-through, pace, and revenue by pricing level for a selected event.',
        path: '/pricing-level-performance',
        icon: 'chart',
        accent: 'accent-pink',
      },
      {
        id: 'revenue-insights',
        title: 'Revenue insights',
        description:
          'Benchmark on-sale events against similar past shows and review AI-generated recommendations.',
        path: '/revenue-insights',
        icon: 'chart',
        accent: 'accent-pink',
      },
      {
        id: 'insights-dashboard',
        title: 'Insights dashboard',
        description:
          'Review actionable recommendation cards for weekday underperformance, sales pace, and more.',
        path: '/insights-dashboard',
        icon: 'chart',
        accent: 'accent-cyan',
      },
      {
        id: 'daily-settlement',
        title: 'Daily settlement report',
        description:
          'End-of-day settlement totals broken down by payment method.',
        path: '/daily-settlement',
        icon: 'receipt',
        accent: 'accent-green',
        tag: 'Finance',
      },
      {
        id: 'summary-by-payment-method',
        title: 'Summary by payment method',
        description:
          'Order count, quantity, gross, and facility fee by payment method and product.',
        path: '/summary-by-payment-method',
        icon: 'receipt',
        accent: 'accent-cyan',
        tag: 'Finance',
      },
      {
        id: 'accounting-dashboard',
        title: 'Accounting dashboard',
        description: 'Daily close KPIs for sales, fees, refunds, and payouts.',
        path: '/accounting-dashboard',
        icon: 'coin',
        accent: 'accent-amber',
        tag: 'Finance',
      },
      {
        id: 'accounting-report',
        title: 'Accounting',
        description:
          'Service fee ledger of collected card fees and owed non-card fees by team.',
        path: '/accounting-report',
        icon: 'coin',
        accent: 'accent-green',
        tag: 'Finance',
        accountingOnly: true,
      },
      {
        id: 'deferred-revenue',
        title: 'Season deferred revenue',
        description:
          'Unearned season ticket liability and month-end recognition rollforward.',
        path: '/deferred-revenue',
        icon: 'coin',
        accent: 'accent-green',
        tag: 'Finance',
      },
      {
        id: 'flex-pack-deferred-revenue',
        title: 'Flex pack deferred revenue',
        description:
          'Unearned flex pack voucher liability and month-end redemption/breakage rollforward.',
        path: '/flex-pack-deferred-revenue',
        icon: 'coin',
        accent: 'accent-green',
        tag: 'Finance',
      },
      {
        id: 'custom-fee-report',
        title: 'Custom fee report',
        description: 'Breakdown of all applied fees by event or order.',
        path: '/custom-fee-report',
        icon: 'coin',
        accent: 'accent-pink',
      },
      {
        id: 'henson-fund-report',
        title: 'Henson Fund report',
        description:
          'Henson Fund custom fee contributions with buyer contact details.',
        path: '/henson-fund-report',
        icon: 'coin',
        accent: 'accent-pink',
        orgOnly: true,
      },
      {
        id: 'payouts-report',
        title: 'Payouts report',
        description: 'Track payout totals, statuses, and transfer timing.',
        path: '/payouts-report',
        icon: 'receipt',
        accent: 'accent-green',
      },
    ],
  },
  {
    id: 'orders-receivables',
    title: 'Orders & Receivables',
    reports: [
      {
        id: 'report-builder',
        title: 'Custom report builder',
        description:
          'Create reusable custom reports with your own columns, filters, and schedules.',
        path: '/report-builder',
        icon: 'chart',
        accent: 'accent-pink',
      },
      {
        id: 'order-listings',
        title: 'Order listings',
        description:
          'Full order history with buyer details and ticket breakdown.',
        path: '/order-listings',
        icon: 'receipt',
        accent: 'accent-cyan',
      },
      {
        id: 'promoter-sales-reports',
        title: 'Promoter sales reports',
        description:
          'Schedule combined ticket-sales summaries for selected events and recipients.',
        path: '/promoter-sales-reports',
        icon: 'calendar',
        accent: 'accent-pink',
      },
      {
        id: 'season-report',
        title: 'Season report',
        description:
          'Season-scoped orders with date range and product type filters.',
        path: '/season-report',
        icon: 'calendar',
        accent: 'accent-green',
      },
      {
        id: 'ar-aging-details',
        title: 'A/R aging details',
        description: 'Outstanding balances by account, aged 30/60/90 days.',
        path: '/ar-aging-details',
        icon: 'calendar',
        accent: 'accent-amber',
        tag: 'Finance',
      },
      {
        id: 'ar-aging-summary',
        title: 'A/R aging summary',
        description: 'High-level view of total receivables by aging bucket.',
        path: '/ar-aging-summary',
        icon: 'calendar',
        accent: 'accent-green',
        tag: 'Finance',
      },
      {
        id: 'sales-receivables-by-category',
        title: 'Sales & receivables by category',
        description:
          'Total sales by category less payments collected equals outstanding receivables.',
        path: '/sales-receivables-by-category',
        icon: 'coin',
        accent: 'accent-amber',
        tag: 'Finance',
      },
      {
        id: 'buyer-types-report',
        title: 'Buyer Types Report',
        description:
          'Orders, sales, collections, and outstanding balances by buyer type.',
        path: '/buyer-types-report',
        icon: 'coin',
        accent: 'accent-green',
        tag: 'Finance',
      },
      {
        id: 'payment-plans-report',
        title: 'Payment plans',
        description: 'Review installment schedules, balances, and plan status.',
        path: '/payment-plans-report',
        icon: 'coin',
        accent: 'accent-amber',
      },
    ],
  },
]

export default function ReportingWrapper() {
  const [searchQuery, setSearchQuery] = useState('')
  const organization = AuthService.getOrg()?.[0]
  const currentUser = AuthService.getUser()?.user
  const showHensonFundReport = isHensonFundReportEnabled(organization?.uuid)
  const paymentPlansEnabled = Boolean(organization?.setting?.paymentPlans)
  const accessPassEnabled = Boolean(organization?.setting?.accessPass)
  const showAccountingReport = canViewAccountingReport({
    organization,
    isSuperAdmin: Boolean(
      currentUser?.superAdmin ||
      AuthService.isSuperAdminOrganizer() ||
      organization?.members?.some(
        (member) => member?.uuid === currentUser?.uuid && member?.superAdmin
      )
    ),
  })

  const sections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const baseSections = REPORTING_SECTIONS.map((section) => ({
      ...section,
      reports: section.reports.filter((report) => {
        if (report.orgOnly && !showHensonFundReport) return false
        if (report.accountingOnly && !showAccountingReport) return false
        if (report.id === 'payment-plans-report' && !paymentPlansEnabled) {
          return false
        }
        if (report.accessPassOnly && !accessPassEnabled) return false
        return true
      }),
    })).filter((section) => section.reports.length > 0)

    if (!query) {
      return baseSections
    }

    return baseSections
      .map((section) => ({
        ...section,
        reports: section.reports.filter((report) => {
          return `${report.title} ${report.description}`
            .toLowerCase()
            .includes(query)
        }),
      }))
      .filter((section) => section.reports.length > 0)
  }, [
    searchQuery,
    showHensonFundReport,
    showAccountingReport,
    paymentPlansEnabled,
    accessPassEnabled,
  ])

  return (
    <section className="spacer-sm reporting-page" id="reporting-wrapper">
      <header className="section-header-sm reporting-page__header">
        <div className="section-heading">
          <h1>Reports</h1>
          <p className="section-header-desc">
            View proceeds, ticket sales, attendance, and financial summaries.
          </p>
        </div>
      </header>

      <div className="reporting-page__filters">
        <Row className="g-3 reporting-page__filters-row">
          <Col xs={12} md={6} lg={4}>
            <Form.Control
              className="reporting-page__control"
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search reports..."
              aria-label="Search reports"
            />
          </Col>
        </Row>
      </div>

      {sections.length > 0 ? (
        <Stack gap={4}>
          {sections.map((section) => (
            <section key={section.id} className="reporting-page__group">
              <h2 className="reporting-page__group-title mb-0">
                {section.title}
              </h2>
              <Card body className="reporting-page__group-card mt-2">
                <Row className="g-3">
                  {section.reports.map((report) => (
                    <Col
                      key={report.id}
                      xs={12}
                      md={6}
                      lg={4}
                      className="d-flex"
                    >
                      <LinkContainer to={report.path}>
                        <Button
                          variant="default"
                          className={`btn-list-view btn-list-view--lg btn-list-view-with-border fw-normal reporting-card ${report.accent || 'accent-cyan'} ${
                            report.selected ? 'selected' : ''
                          }`}
                        >
                          <div
                            className={`reporting-card__icon ${report.icon}`}
                          />
                          <div className="reporting-card__content">
                            <span className="reporting-card__eyebrow">
                              {section.title}
                            </span>
                            <h3>{report.title}</h3>
                            <p>{report.description}</p>
                          </div>
                        </Button>
                      </LinkContainer>
                    </Col>
                  ))}
                </Row>
              </Card>
            </section>
          ))}
        </Stack>
      ) : (
        <Card body>
          <p className="text-muted mb-0">
            No reports match your search. Try a different keyword.
          </p>
        </Card>
      )}
    </section>
  )
}
